import { Box, Stack, Typography } from "@mui/material";
import { useMemo } from "react";

import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton } from "@/components/common";

import { WooCartDraftList } from "./WooCartDraftList";
import { formatPriceBrl } from "../utils/addressHelpers";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import type { CartItem, WooStoreConfig } from "../types";
import { groupCartItemsByStore } from "../utils/storeGroupingHelpers";

interface WooCartModalProps {
  open: boolean;
  onClose: () => void;
  carts: Record<string, CartItem[]>;
  stores: WooStoreConfig[];
  draftSubtotal: number;
  draftItemCount: number;
  checkoutLoading?: boolean;
  checkoutStoreId?: string | null;
  onUpdateDraftQuantity: (
    storeId: string,
    productId: number,
    quantity: number,
  ) => void;
  onCheckout: () => void;
  onCheckoutStore?: (storeId: string) => void;
  onExploreProducts?: () => void;
}

/** Modal do carrinho — rascunho agrupado por loja. */
export function WooCartModal({
  open,
  onClose,
  carts,
  stores,
  draftSubtotal,
  draftItemCount,
  checkoutLoading = false,
  checkoutStoreId = null,
  onUpdateDraftQuantity,
  onCheckout,
  onCheckoutStore,
  onExploreProducts,
}: WooCartModalProps) {
  const groups = useMemo(
    () => groupCartItemsByStore(carts, stores),
    [carts, stores],
  );
  const storeCount = groups.length;
  const multiStore = storeCount > 1;

  const handleClose = () => {
    if (checkoutLoading) return;
    onClose();
  };

  const subtitle =
    draftItemCount > 0
      ? multiStore
        ? `${draftItemCount} ${draftItemCount === 1 ? "item" : "itens"} · ${storeCount} lojas · ${formatPriceBrl(draftSubtotal)}`
        : `${draftItemCount} ${draftItemCount === 1 ? "item" : "itens"} · ${formatPriceBrl(draftSubtotal)}`
      : undefined;

  const footer =
    !multiStore && groups.length > 0 ? (
      <Stack spacing={1.5} width="100%">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ ...mt.body, fontWeight: 700 }}>Subtotal</Typography>
          <Typography sx={{ ...mt.price, color: "primary800.main" }}>
            {formatPriceBrl(draftSubtotal)}
          </Typography>
        </Stack>
        <PrimaryButton
          width="100%"
          height="48px"
          loading={checkoutLoading}
          onClick={onCheckout}
        >
          Finalizar compra
        </PrimaryButton>
      </Stack>
    ) : null;

  return (
    <CommonDialog
      isOpen={open}
      handleClose={handleClose}
      maxWidth="md"
      sheetTitle="Seu carrinho"
      sheetSubtitle={subtitle}
      footer={footer}
      sheetSize="large"
    >
      <Box
        sx={{
          minHeight: { md: 420 },
          maxHeight: { xs: "60vh", md: "75vh" },
          overflowY: "auto",
          px: { xs: 0, md: 0.5 },
          pt: 0.5,
          pb: 0.5,
        }}
      >
        <WooCartDraftList
          carts={carts}
          stores={stores}
          checkoutLoading={checkoutLoading}
          checkoutStoreId={checkoutStoreId}
          onUpdateDraftQuantity={onUpdateDraftQuantity}
          onCheckoutStore={onCheckoutStore}
          onExploreProducts={() => {
            handleClose();
            onExploreProducts?.();
          }}
        />
      </Box>
    </CommonDialog>
  );
}
